import * as request from "request";
import {baseUrl} from "config";
import {IServiceAction, postAction} from "redux-request-middleware";
import {
  addAuth,
  headers as getHeaders,
} from "service";

export enum LoadingActionType {
  ADD = "ADD",
  REMOVE = "REMOVE",
}

interface INotification {
  success?: postAction;
  failure?: postAction;
}

export const removeLoading = (
  array: Array<IServiceAction<any, any, any, any>>,
  element: IServiceAction<any, any, any, any>,
): Array<IServiceAction<any, any, any, any>> => {
  const index = getLoadingIndex(array, element);
  const mArray = array.slice();
  if (index !== -1) {
    mArray.splice(index, 1);
  }
  return mArray;
};

export const addLoading = (
  array: Array<IServiceAction<any, any, any, any>>,
  element: IServiceAction<any, any, any, any>,
): Array<IServiceAction<any, any, any, any>> => {
  const mArray = array.slice();
  if (!isLoading(array, element)) {
    mArray.push(element);
  }
  return mArray;
};

export const getLoadingIndex = (
  array: Array<IServiceAction<any, any, any, any>>,
  element: IServiceAction<any, any, any, any>,
): number => {
  for (let i = 0; i < array.length; i++) {
    if (JSON.stringify(array[i]) === JSON.stringify(element)) {
      return i;
    }
  }
  return -1;
};

export const isLoading = (
  array: Array<IServiceAction<any, any, any, any>>,
  element: IServiceAction<any, any, any, any>,
): boolean => {
  return getLoadingIndex(array, element) !== -1;
};

export function postRequest<Action, B, E>(
  type: Action, url: string,
  body?: B,
  addAuthentication?: boolean,
  extra?: E,
  notification?: INotification,
  baseUrl?: string,
): IServiceAction<Action, B, any, E> {
  // TODO: remove adding authentication in body
  if (addAuthentication) {
    body = addAuth(body);
  }
  return {
    type,
    payload: {
      extra,
      successAction: notification && notification.success ? notification.success : null,
      failureAction: notification && notification.failure ? notification.failure : null,
      service: {
        ...(config(baseUrl)),
        method: "POST",
        url,
        body,
      },
    },
  };
}

export function getRequest<Action, Q, E>(
  type: Action,
  url: string,
  qs?: Q,
  extra?: E,
  notification?: INotification,
  baseUrl?: string,
): IServiceAction<Action, any, Q, E> {
  return {
    type,
    payload: {
      extra,
      successAction: notification && notification.success ? notification.success : null,
      failureAction: notification && notification.failure ? notification.failure : null,
      service: {
        ...(config(baseUrl)),
        method: "GET",
        url,
        qs,
      },
    },
  };
}

export function postRequestFromHost<Action, B, E>(
  type: Action, url: string,
  body?: B,
  addAuthentication?: boolean,
  extra?: E,
  notification?: INotification,
  mHost?: string,
) {
  const mRequest = postRequest(type, url, body, addAuthentication, extra, notification);
  mRequest.payload.service.baseUrl = mHost;
  return mRequest;
}

export function getRequestFromHost<Action, Q, E>(
  type: Action,
  url: string,
  qs?: Q,
  extra?: E,
  notification?: INotification,
  mHost?: string,
) {
  const mRequest = getRequest(type, url, qs, extra, notification);
  mRequest.payload.service.baseUrl = mHost;
  mRequest.payload.service.headers = null;
  return mRequest;
}

export const config = (customBaseUrl?: string): {baseUrl: string, headers: object, json: boolean} => {
  const headers = getHeaders();
  const base = customBaseUrl === undefined ? baseUrl : customBaseUrl;
  return {
    baseUrl: base,
    headers,
    json: true,
  };
};

export function service(options: {}): void {
  request({
    ...(config()),
    ...options,
  });
}

export function isRequestActionUnserved(action: IServiceAction<any, any, any, any>): boolean {
  const {type} = action;
  return type.match(/^REQUEST_/) &&
      !type.match(/_SUCCESS$/) &&
      !type.match(/_FAILURE$/);
}

export function isRequestAction(action: {type?: string}): boolean {
  const {type} = action;
  return type && Boolean(type.match(/^REQUEST_/));
}

export function getLoadingAction(type: string): LoadingActionType {
  return type.match(/_SUCCESS$/) || type.match(/_FAILURE$/) ?
    LoadingActionType.REMOVE :
    LoadingActionType.ADD;
}

export function cleanActionType(type: string): string {
  return type ? type.replace("REQUEST_", "")
        .replace("_SUCCESS", "")
        .replace("_FAILURE", "") : type;
}

export function cleanAction(action: IServiceAction<any, any, any, any>): IServiceAction<any, any, any, any> {
  const mAction = {...action};
  mAction.type = cleanActionType(mAction.type);
  delete mAction.error;
  delete mAction.response;
  delete mAction.body;
  return mAction;
}

export function isActionLoading(loadings: Array<IServiceAction<any, any, any, any>>, type: string): boolean {
  const mType = cleanActionType(type);
  return loadings.some((action) => action.type === mType);
}
